import React, { createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuth } from './SupabaseProvider';

interface ThemeState {
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  userThemes: Record<string, 'light' | 'dark' | 'system'>;
  setUserTheme: (userId: string, theme: 'light' | 'dark' | 'system') => void;
}

const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      userThemes: {},
      setTheme: (theme) => set({ theme }),
      setUserTheme: (userId, theme) => set((state) => ({
        theme,
        userThemes: { ...state.userThemes, [userId]: theme }
      })),
    }),
    {
      name: 'theme-storage',
    }
  )
);

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, setTheme, userThemes, setUserTheme } = useThemeStore();
  const { user } = useAuth();

  useEffect(() => {
    // If user is logged in, use their saved theme preference
    if (user?.id && userThemes[user.id]) {
      setTheme(userThemes[user.id]);
    }
  }, [user, userThemes, setTheme]);

  useEffect(() => {
    // Save user's theme preference
    if (user?.id) {
      setUserTheme(user.id, theme);
    }
  }, [user, theme, setUserTheme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, userThemes, setUserTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};